'use client';
import { cn } from '@wwe-portal/ui/lib/lib';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../dropdown';
import { ClickableDiv } from '../button';
import { IcoMoonIcon } from '../icons';
import { Each } from '../utils';
import { ReactNode, useState } from 'react';

interface SelectProps<T> {
  value: T | null;
  onChange: (value: T) => void;
  options: T[];
  renderItem: (item: T) => ReactNode;
  getKey: (item: T) => string;
  noOption?: ReactNode;
}

export const Select = <T,>({
  value,
  onChange,
  options,
  renderItem,
  noOption,
  getKey,
}: SelectProps<T>) => {
  const [open, setOpen] = useState(false);
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        asChild
        onPointerDown={(e) => e.preventDefault()}
        onClick={() => setOpen(!open)}
      >
        <ClickableDiv
          className={cn(
            'border-back200 bg-back300 text-textColor15 drop-shadow-card flex h-[46px] w-full items-center justify-between rounded border-0 border-l-[1px] border-solid px-4 py-2.5 text-center text-sm font-medium [&[data-state=open]>svg[data-label=arrow]]:text-[#8ae2b6]',
            {
              'bg-back200': open,
            }
          )}
        >
          {value == null ? noOption : renderItem(value)}
          {open && (
            <div className="absolute inset-y-0 left-0 -z-10 w-1/2 overflow-hidden">
              <div
                className="animate-in slide-in-from-left pointer-events-none absolute inset-0"
                style={{
                  backgroundImage:
                    'linear-gradient(90deg, #005840 0%, rgba(0, 95, 69, 0) 100%)',
                }}
              />
            </div>
          )}
          <IcoMoonIcon
            iconName="arrow"
            data-label="arrow"
            className={cn(
              'text-inlineTextColor18 size-4 transition-transform',
              {
                'rotate-180': open,
              }
            )}
          />
        </ClickableDiv>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="bg-back200 text-text600 w-[--radix-popper-anchor-width] px-0 py-0"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
        }}
        align={'start'}
        isQualMenuWidth={true}
      >
        <div className="no-scrollbar pointer-events-auto max-h-[360px] overflow-y-auto">
          <Each
            data={options}
            render={(item) => (
              <DropdownMenuItem
                key={getKey(item)}
                className={cn(
                  'hover:bg-back400 relative cursor-pointer capitalize',
                  {
                    'bg-back300': value && getKey(item) === getKey(value),
                  }
                )}
                onClick={() => onChange(item)}
              >
                <div className="relative z-10 px-2 text-[#A6ACDC]">
                  {renderItem(item)}
                </div>
              </DropdownMenuItem>
            )}
          />
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
